import dayjs from 'dayjs';
import { LoaderCircleIcon, PlusIcon } from 'lucide-react';

import { calculateExpectedCostForScraping } from '../../helpers/calculate-expected-cost-for-scraping';
import { ScrapingJobStatus, type DirectUrlJob } from '../../types/types';
import { PostDownloadButton } from '../GoogleSearchScrapingTable/PostDownloadButton';
import { ScrapingJobStatusBadge } from '../ScrapingJobStatusBadge';
import { showMemoModal } from '../showMemoModal';

import { getPlatformName } from '@/entities/public-filter/constants';
import { Button } from '@/shared/components/shadcn/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/shadcn/table';
import { DATE_FORMATS } from '@/shared/utils/date/date-formats';

interface Props {
  urlScrapingJobs: DirectUrlJob[];
  total?: number;
  isFetching: boolean;
  onCreateNew: () => void;
}

const GRID_TEMPLATE_COLUMNS = '120px 100px 100px 100px 120px 130px 150px 100px 1fr';

export function DirectUrlScrapingTable({
  urlScrapingJobs,
  total = 0,
  isFetching,
  onCreateNew,
}: Props) {
  return (
    <div className="flex flex-col">
      <div className="flex items-end justify-between p-6">
        <div className="flex flex-1 flex-col gap-1">
          <p className="text-xl font-semibold">Direct URL Scraping list ({total})</p>
          <p className="text-muted-foreground text-sm">
            View and manage your URL-based scraping requests. Track the progress of each request and
            stay updated.
          </p>
        </div>
        <Button onClick={onCreateNew} variant="default">
          <PlusIcon />
          Create new
        </Button>
      </div>
      <div className="relative p-6">
        {isFetching && (
          <div className="bg-background/80 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-sm">
            <LoaderCircleIcon className="h-8 w-8 animate-spin" />
          </div>
        )}
        <Table className={isFetching ? 'opacity-50' : ''}>
          <TableHeader>
            <TableRow gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
              <TableHead>Owner</TableHead>
              <TableHead>Platform</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Input URLs</TableHead>
              <TableHead>Scraped URLs</TableHead>
              <TableHead>Amount spent</TableHead>
              <TableHead className="text-right">Requested date</TableHead>
              <TableHead>Download</TableHead>
              <TableHead>Memo</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {urlScrapingJobs.map((job) => (
              <TableRow key={job._id} gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
                <TableCell className="max-w-40 truncate">{job.ownerName.split('@')[0]}</TableCell>
                <TableCell>{getPlatformName(job.platform)}</TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <ScrapingJobStatusBadge status={job.status} />
                  </div>
                </TableCell>
                <TableCell>{job.inputPostUrlCount.toLocaleString()}</TableCell>
                <TableCell>{job.postUrlCount.toLocaleString()}</TableCell>
                <TableCell>
                  ${calculateExpectedCostForScraping(job.scrapedItemCount).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  {dayjs(job.documentCreatedAt).format(DATE_FORMATS.base)}
                </TableCell>
                <TableCell>
                  {job._id && (
                    <PostDownloadButton
                      disabled={job.status === ScrapingJobStatus.InProgress}
                      scrapingJobId={job._id}
                    />
                  )}
                </TableCell>
                <TableCell>
                  {job.memo ? (
                    <Button
                      variant="link"
                      className="h-auto p-0 text-left"
                      onClick={async () => {
                        await showMemoModal({
                          memo: job.memo || '',
                          title: 'Memo',
                        });
                      }}
                    >
                      <span className="max-w-[120px] truncate">{job.memo}</span>
                    </Button>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
